import React from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Stack,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import { LocationOn, ContactPhone, Email } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { FarmOrganizationData, farmFormConfig } from '../types/farmTypes';
import { DESIGN_TOKENS } from '../../../design-system/tokens/colors';

const countryCodes = [
  { code: '+55', country: 'BR' },
  { code: '+1', country: 'US' },
  { code: '+351', country: 'PT' },
  { code: '+34', country: 'ES' },
  { code: '+44', country: 'GB' },
  { code: '+33', country: 'ES' },
  { code: '+49', country: 'DE' },
  { code: '+39', country: 'IT' },
  { code: '+54', country: 'AR' },
  { code: '+598', country: 'AR' },
  { code: '+595', country: 'UY' },
  { code: '+56', country: 'CL' },
  { code: '+57', country: 'CO' },
  { code: '+51', country: 'CO' },
  { code: '+52', country: 'PE' },
  { code: '+58', country: 'VE' },
];

interface FarmLocationContactStepProps {
  formData: FarmOrganizationData;
  handleLocationChange: (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleContactChange: (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleOpenMap: () => void;
}

const FarmLocationContactStep: React.FC<FarmLocationContactStepProps> = ({
  formData,
  handleLocationChange,
  handleContactChange,
  handleOpenMap,
}) => {
  const { t } = useTranslation('forms');

  // Helper to split full phone into code and number
  const getPhoneParts = (fullPhone: string) => {
    if (!fullPhone) return { code: '+55', number: '' };
    const foundCode = countryCodes.find(c => fullPhone.startsWith(c.code));
    if (foundCode) {
      return { 
        code: foundCode.code, 
        number: fullPhone.substring(foundCode.code.length) 
      };
    }
    return { code: '+55', number: fullPhone };
  };

  const { code: phoneCode, number: phoneNumber } = getPhoneParts(formData.contact_info?.phone || '');

  // Simple mask function for phone input
  const formatPhoneNumber = (value: string) => {
    // Remove non-digits
    const numbers = value.replace(/\D/g, '');
    
    // Apply mask based on length
    // (XX) XXXXX-XXXX for mobile (11 digits)
    // (XX) XXXX-XXXX for landline (10 digits)
    if (numbers.length <= 10) {
      return numbers.replace(/^(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
    } else {
      return numbers.replace(/^(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3');
    }
  };

  const handlePhoneCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newCode = event.target.value;
    const newPhone = `${newCode}${phoneNumber}`;
    handleContactChange('phone')({ target: { value: newPhone } } as React.ChangeEvent<HTMLInputElement>);
  };

  const handlePhoneNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value;
    const numbers = rawValue.replace(/\D/g, '');
    
    // Limit to 11 digits (BR standard)
    if (numbers.length > 11) return;
    
    const formatted = formatPhoneNumber(numbers);
    const newPhone = `${phoneCode}${formatted}`;
    handleContactChange('phone')({ target: { value: newPhone } } as React.ChangeEvent<HTMLInputElement>);
  };

  return (
    <Stack spacing={3} sx={{ mt: 1 }}>
      {/* Location Information Group */}
      <Box>
        <Stack spacing={2}>
          <Button 
            variant="outlined" 
            type="button" 
            startIcon={<LocationOn />}
            onClick={handleOpenMap}
            fullWidth
            sx={{
              borderColor: DESIGN_TOKENS.colors.organization.farm,
              color: DESIGN_TOKENS.colors.organization.farm,
              py: 1.5,
              '&:hover': {
                borderColor: DESIGN_TOKENS.colors.organization.farm,
                backgroundColor: `${DESIGN_TOKENS.colors.organization.farm}08`,
              }
            }}
          >
            {formData.location?.coordinates?.latitude !== 0 
              ? t('farm.location_coordinates.change_location', 'Alterar localização no mapa')
              : t('farm.location_coordinates.select_location', 'Selecionar no mapa')}
          </Button>
          
          {formData.location?.coordinates?.latitude !== 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
              Coordenadas selecionadas: {formData.location?.coordinates?.latitude.toFixed(6)}, {formData.location?.coordinates?.longitude.toFixed(6)}
            </Typography>
          )}
        </Stack>
      </Box>

      {/* Contact Information Group */}
      <Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              select
              value={phoneCode}
              onChange={handlePhoneCodeChange}
              sx={{ width: { xs: 100, sm: 120 } }}
              label={t('DDI', 'DDI')}
            >
              {countryCodes.map((option) => (
                <MenuItem key={option.code} value={option.code}>
                  {option.code}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={t('farm.contact_info.phone.label')}
              placeholder="11 99999-9999"
              value={phoneNumber}
              onChange={handlePhoneNumberChange}
              required={farmFormConfig.contact_info.phone.required}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': { borderColor: DESIGN_TOKENS.colors.primary[700] },
                  '&:hover fieldset': { borderColor: DESIGN_TOKENS.colors.primary[700] }
                },
                '& .MuiInputLabel-root.Mui-focused': { color: DESIGN_TOKENS.colors.primary[700] }
              }}
            />
          </Box>
          <TextField
            label={t('farm.contact_info.email.label')}
            placeholder={t('farm.contact_info.email.placeholder')}
            value={formData.contact_info?.email || ''}
            onChange={handleContactChange('email')}
            required={farmFormConfig.contact_info.email.required}
            type="email"
            InputProps={{
              startAdornment: <Email sx={{ mr: 1, color: DESIGN_TOKENS.colors.organization.farm }} />
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': { borderColor: DESIGN_TOKENS.colors.primary[700] },
                '&:hover fieldset': { borderColor: DESIGN_TOKENS.colors.primary[700] }
              },
              '& .MuiInputLabel-root.Mui-focused': { color: DESIGN_TOKENS.colors.primary[700] }
            }}
          />
        </Box>
      </Box>
    </Stack>
  );
};

export default FarmLocationContactStep;
